/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/*!
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@                                         @@@@
 *!   @@@   THIS FILE WAS AUTOGENERATED BY TOOL   @@@@
 *!   @@@   CHANGES MUST TARGET SCRIPTS/SWAGGER   @@@@
 *!   @@@                                         @@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!
 */

import {
  _buildQueryString,
  _emptyParams,
  _encode,
  _getBasePath,
  _getRawBasePath,
} from './urls-utils'

/**
 * POST /api/v1/saas/users
 * Operation: `create-saas-user-admin`
 * Create SaaS user.
 * @see Accepts a request payload of type {@link CreateSaasUserAdminRequest}
 * @see 201 User successfully created. See {@link SaasUserCreationResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 409 User already exists. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSaasUserAdminUrl(): string {
  const path = `/saas/users`
  return `${_getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_invite
 * Operation: `invite-saas-user`
 * Invites a new user with a given email and will apply a specific profile upon acceptance.
 * @see Accepts a request payload of type {@link SaasInviteUserEmailRequest}
 * @see 200 The user invitation was created. See {@link SaasInvitedUserResponse}
 * @see 400 The invited email is an invalid email address. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 409 The invited user already exists. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function inviteSaasUserUrl(): string {
  const path = `/saas/users/_invite`
  return `${_getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_resend_verification
 * Operation: `resend-saas-verification-email-admin`
 * Resends the email for the user to verify their email address.
 * @see Accepts a request payload of type {@link SaasResendVerificationRequest}
 * @see 200 Verification email resent
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @see 409 User has already verified their email. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resendSaasVerificationEmailAdminUrl(): string {
  const path = `/saas/users/_resend_verification`
  return `${_getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_search
 * Operation: `search-saas-users`
 * Search for users by email. Returns the top 100 results.
 * @see Accepts a request payload of type {@link SaasUsersSearchRequestData}
 * @see 200 Search successful. See {@link Array<SaasUsersSearchResponseData>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchSaasUsersUrl(): string {
  const path = `/saas/users/_search`
  return `${_getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/external/events/verify_email
 * Operation: `add-saas-external-user-event`
 * Post external user verify-email event.
 * @see Accepts a request payload of type {@link SaasExternalChangeMailEvent}
 * @see 201 External User event successfully created. See {@link SaasExternalUserResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addSaasExternalUserEventUrl(): string {
  const path = `/saas/users/external/events/verify_email`
  return `${_getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/users/external/{email}
 * Operation: `get-saas-external-user`
 * Fetch external user info.
 * @param {string} params.email Email for the user.
 * @see 200 External User successfully fetched. See {@link SaasExternalUserResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 External User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasExternalUserUrl(params: GetSaasExternalUserUrlParams): string {
  const path = _encode`/saas/users/external/${params.email}`
  return `${_getRawBasePath()}${path}`
}

export interface GetSaasExternalUserUrlParams {
  email: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/users/{user_ids}/authorization/_check
 * Operation: `check-authorization-on-behalf-of`
 * Check authorisation on behalf of a number users. Note that since this endpoint allows targeting multiple users, rather than fail on any non-existence user ids, the results for such users will be 'not-authorised'.
 * @param {string} params.userIds Identifiers for users in CSV form.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AuthzCheckRequest}
 * @see 200 Authorization check results. See {@link OnBehalfOfUserAuthzCheckResponses}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @see 403 Not authorised to run this endpoint. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function checkAuthorizationOnBehalfOfUrl(
  params: CheckAuthorizationOnBehalfOfUrlParams,
): string {
  const path = _encode`/${params.regionId}/saas/users/${params.userIds}/authorization/_check`
  return `${_getBasePath()}${path}`
}

export interface CheckAuthorizationOnBehalfOfUrlParams {
  userIds: string
  regionId: string
}

/**
 * GET /api/v1/saas/users/{user_id}
 * Operation: `get-saas-user`
 * Fetch user info.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.include Comma delimited list of optional properties to include in the output.
 * @see 200 User successfully fetched. See {@link SaasUserResponse}
 * @see 400 Invalid user id. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserUrl(params: GetSaasUserUrlParams): string {
  const queryString = _buildQueryString({
    include: params.include,
  })
  const path = _encode`/saas/users/${params.userId}`
  return `${_getRawBasePath()}${path}${queryString}`
}

export interface GetSaasUserUrlParams {
  userId: string
  include?: string | null
}

/**
 * GET /api/v1/saas/users/{user_id}/_password_params
 * Operation: `get-saas-reset-password-params`
 * Fetches reset password params for the user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 reset password params successfully fetched. See {@link SaasResetPasswordLinkParamsResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasResetPasswordParamsUrl(params: GetSaasResetPasswordParamsUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/_password_params`
  return `${_getRawBasePath()}${path}`
}

export interface GetSaasResetPasswordParamsUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/_request_deletion
 * Operation: `request-saas-user-deletion`
 * Requests that a user be deleted.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The request was processed successfully.
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function requestSaasUserDeletionUrl(params: RequestSaasUserDeletionUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/_request_deletion`
  return `${_getRawBasePath()}${path}`
}

export interface RequestSaasUserDeletionUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/_scrub
 * Operation: `scrub-saas-user`
 * Scrubs personal identifiable information for a user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The user information was successfully scrubbed. See {@link SaasUserResponse}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function scrubSaasUserUrl(params: ScrubSaasUserUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/_scrub`
  return `${_getRawBasePath()}${path}`
}

export interface ScrubSaasUserUrlParams {
  userId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/mfa
 * Operation: `get-saas-user-mfa-enabled`
 * Fetch a user's MFA enabled status.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The MFA enabled/disabled state response. See {@link SaasAuthMfaEnabledResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserMfaEnabledUrl(params: GetSaasUserMfaEnabledUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa`
  return `${_getRawBasePath()}${path}`
}

export interface GetSaasUserMfaEnabledUrlParams {
  userId: string
}

/**
 * PUT /api/v1/saas/users/{user_id}/auth/mfa
 * Operation: `set-saas-user-mfa-enabled`
 * Enables or disables MFA for a user.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnabledRequest}
 * @see 200 The MFA enabled/disabled state response. See {@link SaasAuthMfaEnabledResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasUserMfaEnabledUrl(params: SetSaasUserMfaEnabledUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa`
  return `${_getRawBasePath()}${path}`
}

export interface SetSaasUserMfaEnabledUrlParams {
  userId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/mfa/devices
 * Operation: `get-saas-user-mfa-devices`
 * Returns all available MFA devices for a user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The MFA devices response. See {@link SaasAuthMfaDevicesResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserMfaDevicesUrl(params: GetSaasUserMfaDevicesUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa/devices`
  return `${_getRawBasePath()}${path}`
}

export interface GetSaasUserMfaDevicesUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/auth/mfa/devices
 * Operation: `enroll-saas-user-mfa-device`
 * Enrolls a new MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnrollDeviceRequest}
 * @see 200 The MFA device enrollment response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enrollSaasUserMfaDeviceUrl(params: EnrollSaasUserMfaDeviceUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa/devices`
  return `${_getRawBasePath()}${path}`
}

export interface EnrollSaasUserMfaDeviceUrlParams {
  userId: string
}

/**
 * PUT /api/v1/saas/users/{user_id}/auth/mfa/devices/{device_id}
 * Operation: `activate-saas-user-mfa-device`
 * Activates an enrolled MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaActivateDeviceRequest}
 * @see 200 The MFA device activation response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function activateSaasUserMfaDeviceUrl(params: ActivateSaasUserMfaDeviceUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa/devices/${params.deviceId}`
  return `${_getRawBasePath()}${path}`
}

export interface ActivateSaasUserMfaDeviceUrlParams {
  userId: string
  deviceId: string
}

/**
 * DELETE /api/v1/saas/users/{user_id}/auth/mfa/devices/{device_id}
 * Operation: `delete-saas-user-mfa-device`
 * Deletes an enrolled MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see 200 The MFA device deleted response. See {@link SaasAuthMfaDeviceDeletedResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSaasUserMfaDeviceUrl(params: DeleteSaasUserMfaDeviceUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/mfa/devices/${params.deviceId}`
  return `${_getRawBasePath()}${path}`
}

export interface DeleteSaasUserMfaDeviceUrlParams {
  userId: string
  deviceId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/okta/groups
 * Operation: `get-saas-user-okta-groups`
 * Get a list of a user okta groups.
 * @param {string} params.userId Identifier for the user.
 * @see 200 List of user's okta groups. See {@link SaasUserOktaGroupsResponse}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserOktaGroupsUrl(params: GetSaasUserOktaGroupsUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/okta/groups`
  return `${_getRawBasePath()}${path}`
}

export interface GetSaasUserOktaGroupsUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/auth/okta/groups/{group_name}
 * Operation: `enroll-saas-user-in-okta-group`
 * Enroll the user in an okta group.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.groupName Name of the okta group to enroll the user into.
 * @see 200 List of use's okta groups. See {@link SaasAuthResponse}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enrollSaasUserInOktaGroupUrl(params: EnrollSaasUserInOktaGroupUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/auth/okta/groups/${params.groupName}`
  return `${_getRawBasePath()}${path}`
}

export interface EnrollSaasUserInOktaGroupUrlParams {
  userId: string
  groupName: string
}

/**
 * GET /api/v1/saas/users/{user_id}/email/{email_domain}/suppressions
 * Operation: `get-saas-user-email-suppressions`
 * Get list of email suppressions for given user and domain.
 * @param {string} params.email Override email to retrieve suppressions for.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.emailDomain Email domain for the suppression.
 * @see 200 Suppressions successfully retrieved. See {@link SaasUserEmailSuppressions}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserEmailSuppressionsUrl(
  params: GetSaasUserEmailSuppressionsUrlParams,
): string {
  const queryString = _buildQueryString({
    email: params.email,
  })
  const path = _encode`/saas/users/${params.userId}/email/${params.emailDomain}/suppressions`
  return `${_getRawBasePath()}${path}${queryString}`
}

export interface GetSaasUserEmailSuppressionsUrlParams {
  email?: string | null
  userId: string
  emailDomain: string
}

/**
 * DELETE /api/v1/saas/users/{user_id}/email/{email_domain}/suppressions
 * Operation: `delete-saas-user-email-suppressions`
 * Remove email suppressions for given user and domain.
 * @param {string} params.email Override email to remove suppressions for.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.emailDomain Email domain for the suppression.
 * @see 200 Suppressions successfully deleted
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSaasUserEmailSuppressionsUrl(
  params: DeleteSaasUserEmailSuppressionsUrlParams,
): string {
  const queryString = _buildQueryString({
    email: params.email,
  })
  const path = _encode`/saas/users/${params.userId}/email/${params.emailDomain}/suppressions`
  return `${_getRawBasePath()}${path}${queryString}`
}

export interface DeleteSaasUserEmailSuppressionsUrlParams {
  email?: string | null
  userId: string
  emailDomain: string
}

/**
 * POST /api/v1/saas/users/{user_id}/mfa/_reset
 * Operation: `reset-saas-user-mfa`
 * Resets MFA for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Optional device ID to reset. If not provided this as a query parameter, all devices will be reset.
 * @see 200 The MFA was successfully reseted
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resetSaasUserMfaUrl(params: ResetSaasUserMfaUrlParams): string {
  const queryString = _buildQueryString({
    device_id: params.deviceId,
  })
  const path = _encode`/saas/users/${params.userId}/mfa/_reset`
  return `${_getRawBasePath()}${path}${queryString}`
}

export interface ResetSaasUserMfaUrlParams {
  userId: string
  deviceId?: string | null
}

/**
 * PUT /api/v1/saas/users/{user_id}/profile
 * Operation: `put-saas-user`
 * Update a user's profile as an admin.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasUpdateProfileRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putSaasUserUrl(params: PutSaasUserUrlParams): string {
  const path = _encode`/saas/users/${params.userId}/profile`
  return `${_getRawBasePath()}${path}`
}

export interface PutSaasUserUrlParams {
  userId: string
}
